import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Grid2 as Grid,
  Alert,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  Copy01Icon,
  Delete02Icon,
  Globe02Icon,
  Mail01Icon,
  SmartPhone01Icon,
  Store01Icon,
  UserSquareIcon,
  ViewIcon,
  Share01Icon,
  Tick01Icon,
  Clock01Icon,
  AlertCircleIcon,
  MailSend01Icon,
} from "@hugeicons/core-free-icons";
import { doc, getDoc, collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import { db } from "@/firebase";
import PageHeader from "@/components/PageHeader";
import useSnackbar from "@/hooks/useSnackbar";
import { getInitials, stringToColor } from "@/utils/names";
import { PlanType } from "@/types";
import { useAppSelector } from "@/hooks";
import { deleteOrganizationComplete } from "@/features/organizations/utils/deleteOrganizationData";
import { useSendInviteMutation } from "@/api/accessApi";
import { format } from "date-fns";

interface InvitationData {
  id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "failed";
  createdAt?: number;
}

interface ClientDetailState {
  id: string;
  name: string;
  email: string;
  phone?: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  organizationId?: string;
  organizationTitle?: string;
  slogan?: string;
  industry?: string;
  alias?: string;
  logo?: string;
  isotype?: string;
  primaryColor?: string;
  secondaryColor?: string;
  whatsapp?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  x?: string;
  plan: PlanType;
  onboardingStage: "Completado" | "Organización Creada" | "Autenticado" | "Registrado";
  createdAt?: number;
  invitations: InvitationData[];
}

const ClientDetailsPage = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role === "super-admin";

  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState<ClientDetailState | null>(null);
  const [sendInvite, { isLoading: isSendingInvite }] = useSendInviteMutation();

  const loadClientData = async () => {
    if (!clientId) return;
    setLoading(true);

    try {
      let orgId = "";
      let orgData: any = null;
      let userId = "";
      let userData: any = null;
      let alias = "";
      const invitations: InvitationData[] = [];

      // 1. Primary lookup: Check if clientId is an organization ID
      try {
        const orgDoc = await getDoc(doc(db, "organizations", clientId));
        if (orgDoc.exists()) {
          orgId = orgDoc.id;
          orgData = orgDoc.data();
          if (orgData.alias) alias = orgData.alias;
          if (orgData.createdBy) userId = orgData.createdBy;
        }
      } catch (err) {
        console.warn("Could not fetch organization directly by ID:", err);
      }

      // 2. Secondary lookup: Check if clientId is a user ID
      if (!orgId) {
        try {
          const userDoc = await getDoc(doc(db, "users", clientId));
          if (userDoc.exists()) {
            userId = userDoc.id;
            userData = userDoc.data();

            // Find organizations created by this user
            const orgsSnap = await getDocs(
              query(collection(db, "organizations"), where("createdBy", "==", userId))
            );
            if (!orgsSnap.empty) {
              orgId = orgsSnap.docs[0].id;
              orgData = orgsSnap.docs[0].data();
              if (orgData.alias) alias = orgData.alias;
            }
          }
        } catch (err) {
          console.warn("Could not fetch user directly by ID:", err);
        }
      }

      // 3. Fallback: Search organizations where alias == clientId
      if (!orgId) {
        try {
          const aliasOrgSnap = await getDocs(
            query(collection(db, "organizations"), where("alias", "==", clientId))
          );
          if (!aliasOrgSnap.empty) {
            orgId = aliasOrgSnap.docs[0].id;
            orgData = aliasOrgSnap.docs[0].data();
            alias = orgData.alias || clientId;
            if (orgData.createdBy) userId = orgData.createdBy;
          }
        } catch (err) {
          console.warn("Could not query organization by alias:", err);
        }
      }

      // If neither organization nor user was found
      if (!orgId && !userId) {
        setClientData(null);
        setLoading(false);
        return;
      }

      // 4. If we have orgId and no userId yet, check access subcollection
      if (orgId && !userId) {
        try {
          const accessSnap = await getDocs(collection(db, "organizations", orgId, "access"));
          if (!accessSnap.empty) {
            userId = accessSnap.docs[0].id;
            const accessData = accessSnap.docs[0].data();
            if (!userData && (accessData.userEmail || accessData.userName)) {
              userData = {
                name: accessData.userName,
                email: accessData.userEmail,
              };
            }
          }
        } catch (err) {
          console.warn("Could not fetch organization access subcollection:", err);
        }
      }

      // 5. If we have orgId and no alias, query aliases collection
      if (orgId && !alias) {
        try {
          const aliasSnap = await getDocs(
            query(collection(db, "aliases"), where("organizationId", "==", orgId))
          );
          if (!aliasSnap.empty) {
            alias = aliasSnap.docs[0].id;
          }
        } catch (err) {
          console.warn("Could not fetch alias:", err);
        }
      }

      // 6. Fetch user data if we found a userId
      if (userId && !userData) {
        try {
          const uDoc = await getDoc(doc(db, "users", userId));
          if (uDoc.exists()) {
            userData = uDoc.data();
          }
        } catch (err) {
          console.warn("Could not fetch user document:", err);
        }
      }

      // 7. Fetch invitations for this organization
      if (orgId) {
        try {
          const invSnap = await getDocs(
            query(collection(db, "invitations"), where("orgId", "==", orgId))
          );
          invSnap.forEach((d) => {
            const inv = d.data();
            invitations.push({
              id: d.id,
              email: inv.email,
              role: inv.role || "admin",
              status: inv.status || "pending",
              createdAt:
                inv.createdAt instanceof Timestamp
                  ? inv.createdAt.toMillis()
                  : typeof inv.createdAt === "number"
                    ? inv.createdAt
                    : Date.now(),
            });
          });
        } catch (err) {
          console.warn("Could not fetch invitations:", err);
        }
      }

      // Determine Onboarding Stage
      let stage: ClientDetailState["onboardingStage"] = "Registrado";
      if (orgId) {
        if (alias) {
          stage = "Completado";
        } else {
          stage = "Organización Creada";
        }
      } else if (userId) {
        stage = "Autenticado";
      }

      const displayName = orgData?.title
        ? orgData.title
        : userData?.name
          ? userData.lastname
            ? `${userData.name} ${userData.lastname}`
            : userData.name
          : "Sin nombre";

      const userName = userData?.name
        ? userData.lastname
          ? `${userData.name} ${userData.lastname}`
          : userData.name
        : "";

      setClientData({
        id: clientId,
        name: displayName,
        email: userData?.email || orgData?.email || "",
        phone: userData?.phone || orgData?.phone || orgData?.whatsapp || "",
        userName: userName || displayName,
        userEmail: userData?.email || "",
        userPhone: userData?.phone || "",
        organizationId: orgId || undefined,
        organizationTitle: orgData?.title || "",
        slogan: orgData?.slogan || "",
        industry: orgData?.industry || "",
        alias: alias || undefined,
        logo: orgData?.logo || undefined,
        isotype: orgData?.isotype || undefined,
        primaryColor: orgData?.primaryColor || undefined,
        secondaryColor: orgData?.secondaryColor || undefined,
        whatsapp: orgData?.whatsapp || "",
        facebook: orgData?.facebook || "",
        instagram: orgData?.instagram || "",
        tiktok: orgData?.tiktok || "",
        x: orgData?.x || "",
        plan: (orgData?.plan as PlanType) || PlanType.FREE,
        onboardingStage: stage,
        createdAt: orgData?.createdAt || userData?.createdAt || undefined,
        invitations,
      });
    } catch (error) {
      console.error("Error loading client data:", error);
      openSnackbar("Error al cargar los detalles del cliente", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      openSnackbar(`${label} copiado al portapapeles`, "success");
    } catch (err) {
      console.error(`Error al copiar ${label}:`, err);
    }
  };

  const handleResendInvite = async (email: string, role: string) => {
    if (!clientData?.organizationId) return;

    try {
      await sendInvite({
        email,
        role: role as any,
        orgId: clientData.organizationId,
      }).unwrap();
      openSnackbar(`Invitación reenviada a ${email}`, "success");
      loadClientData();
    } catch (err: any) {
      console.error("Error al reenviar invitación:", err);
      openSnackbar(err?.data?.message || "Error al enviar la invitación", "error");
    }
  };

  const handleDeleteOrganization = async () => {
    if (!clientData?.organizationId) {
      openSnackbar("Esta cuenta no tiene una organización asociada", "warning");
      return;
    }

    const userInput = window.prompt(
      `¿Estás seguro de que deseas eliminar la organización "${clientData.organizationTitle || clientData.name}" con todos sus datos y alias?\n\nPara confirmar, escribe el ID de la organización: ${clientData.organizationId}`
    );

    if (userInput === null) return;

    if (userInput.trim() !== clientData.organizationId) {
      openSnackbar("El ID ingresado no coincide. Operación cancelada.", "error");
      return;
    }

    try {
      openSnackbar("Eliminando organización...", "info");
      await deleteOrganizationComplete(clientData.organizationId);
      openSnackbar("Organización eliminada con éxito", "success");
      navigate("/super-admin/clients");
    } catch (err) {
      console.error("Error al eliminar la organización:", err);
      openSnackbar("Ocurrió un error al eliminar la organización", "error");
    }
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, height: "70vh" }}
        spacing={2}
      >
        <CircularProgress size={44} />
        <Typography variant="body2" color="text.secondary">
          Cargando detalles del cliente...
        </Typography>
      </Stack>
    );
  }

  if (!clientData) {
    return (
      <Stack p={3} spacing={3}>
        <Alert severity="warning">No se encontró información del cliente especificado.</Alert>
      </Stack>
    );
  }

  const stageColorMap: Record<string, "success" | "info" | "warning" | "default"> = {
    Completado: "success",
    "Organización Creada": "info",
    Autenticado: "warning",
    Registrado: "default",
  };

  return (
    <>
      <PageHeader
        title="Detalles del cliente"
        action={
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {isSuperAdmin && clientData.organizationId && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<HugeiconsIcon icon={Delete02Icon} size={16} />}
                onClick={handleDeleteOrganization}
              >
                Eliminar
              </Button>
            )}

            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!clientData.alias}
              startIcon={<HugeiconsIcon icon={Globe02Icon} size={16} />}
              component="a"
              href={clientData.alias ? `https://bitelink.app/${clientData.alias}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver perfil
            </Button>
          </Stack>
        }
      />
      <Stack p={{ xs: 1.5, sm: 2, md: 3 }} spacing={2.5} sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {/* Left Column: Profile & Main Status Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 2,
              }}
            >
              {clientData.logo ? (
                <Box
                  component="img"
                  src={clientData.logo}
                  alt={clientData.name}
                  sx={{
                    width: { xs: 80, sm: 96 },
                    height: { xs: 80, sm: 96 },
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid",
                    borderColor: "divider",
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: { xs: 80, sm: 96 },
                    height: { xs: 80, sm: 96 },
                    fontSize: { xs: 26, sm: 32 },
                    fontWeight: 700,
                    backgroundColor: stringToColor(clientData.name),
                  }}
                >
                  {getInitials(clientData.name)}
                </Avatar>
              )}

              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={700}>
                  {clientData.name}
                </Typography>
                {clientData.slogan && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    "{clientData.slogan}"
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                <Chip
                  size="small"
                  label={clientData.onboardingStage}
                  color={stageColorMap[clientData.onboardingStage]}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  label={clientData.plan === PlanType.PRO ? "Plan Pro" : "Plan Free"}
                  color={clientData.plan === PlanType.PRO ? "primary" : "default"}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>

              {clientData.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  Registrado el {format(clientData.createdAt, "dd/MM/yyyy HH:mm")}
                </Typography>
              )}
            </Paper>

            {/* User Contact Information */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mt: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                CONTACTO DE USUARIO
              </Typography>

              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Stack spacing={0.5} minWidth={0}>
                    <Typography variant="caption" color="text.secondary">
                      Correo Electrónico
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
                      {clientData.email || clientData.userEmail || "—"}
                    </Typography>
                  </Stack>
                  {(clientData.email || clientData.userEmail) && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(clientData.email || clientData.userEmail, "Correo")
                      }
                    >
                      <HugeiconsIcon icon={Copy01Icon} size={16} />
                    </IconButton>
                  )}
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Stack spacing={0.5} minWidth={0}>
                    <Typography variant="caption" color="text.secondary">
                      Teléfono / WhatsApp
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
                      {clientData.phone || clientData.userPhone || clientData.whatsapp || "—"}
                    </Typography>
                  </Stack>
                  {(clientData.phone || clientData.userPhone || clientData.whatsapp) && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(
                          clientData.phone || clientData.userPhone || clientData.whatsapp || "",
                          "Teléfono"
                        )
                      }
                    >
                      <HugeiconsIcon icon={Copy01Icon} size={16} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column: Organization Details, Brand Identity & Invitations */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* Organization Info Card */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                  INFORMACIÓN DEL COMERCIO
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={0.5} minWidth={0}>
                      <Typography variant="caption" color="text.secondary">
                        Nombre de la Organización
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                        {clientData.organizationTitle || "—"}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={0.5} minWidth={0}>
                      <Typography variant="caption" color="text.secondary">
                        Tipo de Comercio
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {clientData.industry === "restaurant" ||
                          clientData.industry === "food-and-beverages"
                          ? "Restaurante"
                          : clientData.industry === "other"
                            ? "Comercio / Tienda"
                            : clientData.industry || "—"}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Stack spacing={0.5} minWidth={0}>
                        <Typography variant="caption" color="text.secondary">
                          ID de Organización
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                          {clientData.organizationId || "—"}
                        </Typography>
                      </Stack>
                      {clientData.organizationId && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopy(clientData.organizationId as string, "ID")
                          }
                        >
                          <HugeiconsIcon icon={Copy01Icon} size={16} />
                        </IconButton>
                      )}
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={0.5} minWidth={0}>
                      <Typography variant="caption" color="text.secondary">
                        Enlace Público
                      </Typography>
                      {clientData.alias ? (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Link
                            href={`https://bitelink.app/${clientData.alias}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                              textDecoration: "none",
                              wordBreak: "break-all",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            https://bitelink.app/{clientData.alias}
                          </Link>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleCopy(`https://bitelink.app/${clientData.alias}`, "Enlace")
                            }
                          >
                            <HugeiconsIcon icon={Copy01Icon} size={14} />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Sin alias configurado
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Brand Identity Card (Colors & Images) */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                  IDENTIDAD VISUAL
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Colores de Marca
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: clientData.primaryColor || "#06175D",
                              border: "1px solid rgba(0,0,0,0.1)",
                            }}
                          />
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {clientData.primaryColor || "#06175D"}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: clientData.secondaryColor || "#D0F857",
                              border: "1px solid rgba(0,0,0,0.1)",
                            }}
                          />
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {clientData.secondaryColor || "#D0F857"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Redes Sociales
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap gap={0.5}>
                        {clientData.whatsapp && (
                          <Chip size="small" label={`WA: ${clientData.whatsapp}`} />
                        )}
                        {clientData.instagram && (
                          <Chip size="small" label={`IG: @${clientData.instagram}`} />
                        )}
                        {clientData.facebook && (
                          <Chip size="small" label={`FB: ${clientData.facebook}`} />
                        )}
                        {clientData.tiktok && (
                          <Chip size="small" label={`TT: @${clientData.tiktok}`} />
                        )}
                        {clientData.x && <Chip size="small" label={`X: @${clientData.x}`} />}
                        {!clientData.whatsapp &&
                          !clientData.instagram &&
                          !clientData.facebook &&
                          !clientData.tiktok &&
                          !clientData.x && (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              Sin redes registradas
                            </Typography>
                          )}
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Invitations & Access Management */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                  INVITACIONES Y ACCESO DE ADMINISTRADOR
                </Typography>

                {clientData.invitations.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No hay invitaciones enviadas para esta organización. Puedes gestionar accesos
                    directamente desde el panel de la organización.
                  </Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {clientData.invitations.map((inv) => (
                      <Paper
                        key={inv.id}
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "grey.50",
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                          <HugeiconsIcon icon={Mail01Icon} size={20} />
                          <Stack spacing={0.25} minWidth={0}>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all" }}>
                              {inv.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Rol: {inv.role === "admin" ? "Administrador" : inv.role}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "space-between", sm: "flex-end" } }}
                        >
                          <Chip
                            size="small"
                            icon={
                              inv.status === "accepted" ? (
                                <HugeiconsIcon icon={Tick01Icon} size={14} />
                              ) : inv.status === "pending" ? (
                                <HugeiconsIcon icon={Clock01Icon} size={14} />
                              ) : (
                                <HugeiconsIcon icon={AlertCircleIcon} size={14} />
                              )
                            }
                            label={
                              inv.status === "accepted"
                                ? "Aceptada"
                                : inv.status === "pending"
                                  ? "Pendiente"
                                  : "Fallida"
                            }
                            color={
                              inv.status === "accepted"
                                ? "success"
                                : inv.status === "pending"
                                  ? "warning"
                                  : "error"
                            }
                          />

                          {inv.status !== "accepted" && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<HugeiconsIcon icon={MailSend01Icon} size={14} />}
                              disabled={isSendingInvite}
                              onClick={() => handleResendInvite(inv.email, inv.role)}
                            >
                              Reenviar
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};

export default ClientDetailsPage;
